import Axios from "../../lib/Axios";
import { getBadgeList as getBadgeListFromBadgeApi } from "../badge/badgeApi";

/**
 * User Badge API Helper
 * 
 * This file contains all API calls related to User Badge management.
 */

/**
 * Get user moderation list
 * @param {Object} filters - Filter parameters
 * @param {string} filters.userId - User ID to search
 * @param {string} filters.keyword - Keyword to search (username)
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @param {string} filters.dateRange - Date range filter (last_24_hours, last_7_days, last_30_days)
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {boolean|string} filters.isSuspended - Suspension status (true, false, or undefined)
 * @returns {Promise} API response with users array and pagination
 */
export const getUserModerationList = async (filters = {}) => {
 
  const {
    userId,
    keyword,
    limit = 50,
    page = 1,
    dateRange,
    startDate,
    endDate,
    isSuspended,
  } = filters;

  // Build query parameters object
  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };
  if (userId && userId.trim() !== "") params.userId = userId.trim();
  if (keyword && keyword.trim() !== "") params.keyword = keyword.trim();
  if (dateRange) params.dateRange = dateRange;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (isSuspended !== undefined && isSuspended !== "") {
    // Convert string "true"/"false" to boolean, or keep boolean as is
    if (isSuspended === "true" || isSuspended === true) {
      params.isSuspended = true;
    } else if (isSuspended === "false" || isSuspended === false) {
      params.isSuspended = false;
    }
  }


  // GET /admin/users
  const result = await Axios.get("admin/users", { params });
  const users = result?.data?.result?.users || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    data: users,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Get badge list - uses the same function from badge API
 * @param {Object} filters - Filter parameters
 * @param {number} filters.limit - Pagination limit (default: 100 for dropdown)
 * @param {number} filters.page - Pagination page number
 * @returns {Promise} API response with badges array
 */
export const getBadgeList = async (filters = {}) => {
  const {
    limit = 100, // Get badges for dropdown
    page = 1,
  } = filters;

  // Use the same getBadgeList function from badge API
  return await getBadgeListFromBadgeApi({ limit, page });
};

/**
 * Add badge to user(s)
 * @param {Object} data - Badge assignment data
 * @param {Array<string>} data.userIds - Array of user IDs
 * @param {number} data.badge_id - The badge ID to assign
 * @returns {Promise} API response with assignment results
 */
export const addBadgeToUser = async (data) => {
  const { userIds, badge_id } = data;
  
  if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
    throw new Error("userIds array is required");
  }
  if (!badge_id) {
    throw new Error("badge_id is required");
  }

  // POST /cms/cms-admin/add-badge-to-user
  const result = await Axios.post("cms/cms-admin/add-badge-to-user", {
    userIds,
    badge_id,
  });
  
  return result?.data || result;
};

/**
 * Remove badge from user(s)
 * @param {Object} data - Badge removal data
 * @param {Array<string>} data.userIds - Array of user IDs
 * @param {number} data.badge_id - The badge ID to remove
 * @returns {Promise} API response with removal results
 */
export const removeBadgeFromUser = async (data) => {
  const { userIds, badge_id } = data;
  
  if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
    throw new Error("userIds array is required");
  }
  if (!badge_id) {
    throw new Error("badge_id is required");
  }

  // POST /cms/cms-admin/remove-badge-from-user
  const result = await Axios.post("cms/cms-admin/remove-badge-from-user", {
    userIds,
    badge_id,
  });
  
  return result?.data || result;
};